/**
 * Variant helper utilities — shared by the workspace UI (route + child
 * components). All platform metadata lives here so we only maintain one
 * canonical table of labels + compose URLs + host-validation domains.
 *
 * Kept in `features/off-page-briefs/lib/` so it stays feature-local per §8 of
 * `docs/architecture/coding-principles.md` (feature-first layout).
 */
import type {
  OffPageBriefPlatform,
  OffPageBriefVariant,
} from '@seo/types';

/**
 * Friendly platform labels for row headers + badges. `linkedin_post` /
 * `linkedin_article` are split because the workspace surfaces them as
 * separate variants and the user needs to know which one they're editing.
 */
export const PLATFORM_LABELS: Record<OffPageBriefPlatform, string> = {
  reddit: 'Reddit',
  quora: 'Quora',
  linkedin_post: 'LinkedIn post',
  linkedin_article: 'LinkedIn article',
  medium: 'Medium',
  dev_to: 'Dev.to',
  x_thread: 'X thread',
};

/** More descriptive label including mode ("Reddit fresh post" vs "comment"). */
export function variantRowLabel(variant: OffPageBriefVariant): string {
  const platform = PLATFORM_LABELS[variant.platform];
  if (variant.mode === 'comment_on_thread') return `${platform} comment`;
  // "fresh_post" is the default; drop the qualifier for LinkedIn / Medium
  // where "post"/"article" is already baked into the label.
  if (
    variant.platform === 'linkedin_post' ||
    variant.platform === 'linkedin_article' ||
    variant.platform === 'medium' ||
    variant.platform === 'dev_to' ||
    variant.platform === 'x_thread'
  ) {
    return platform;
  }
  return `${platform} fresh post`;
}

interface VariantTarget {
  subreddit?: string;
  question_url?: string;
  thread_url?: string;
  hashtags?: string[];
}

function readTarget(target: unknown): VariantTarget {
  if (target && typeof target === 'object') return target as VariantTarget;
  return {};
}

/**
 * Deep-link into each platform's native compose flow. Used by the Copy
 * action on the Content tab: after copying the body, we open the URL in a
 * new tab so the tenant can paste immediately.
 *
 * When a `target` field is present (subreddit picked, thread URL known),
 * the URL points at the specific destination. Otherwise it falls back to
 * the platform-generic compose page.
 */
export function platformComposeUrl(variant: OffPageBriefVariant): string {
  const t = readTarget(variant.target);
  switch (variant.platform) {
    case 'reddit':
      return t.subreddit
        ? `https://www.reddit.com/r/${t.subreddit}/submit`
        : 'https://www.reddit.com/submit';
    case 'quora':
      return t.question_url ?? 'https://www.quora.com';
    case 'linkedin_post':
      return 'https://www.linkedin.com/feed/';
    case 'linkedin_article':
      return 'https://www.linkedin.com/article/new/';
    case 'medium':
      return 'https://medium.com/new-story';
    case 'dev_to':
      return 'https://dev.to/new';
    case 'x_thread':
      return 'https://x.com/compose/post';
  }
}

/**
 * Canonical host per platform — mirrors the API-side `PLATFORM_HOSTS` map in
 * `apps/api/src/routes/off-page-briefs.ts`. We only surface a single host per
 * platform for the client-side pre-check; the API accepts subdomains via
 * `.endsWith`, so `en.wikipedia.org`-style edge cases still validate on save.
 */
export function canonicalHostForPlatform(
  platform: OffPageBriefPlatform,
): string {
  switch (platform) {
    case 'reddit':
      return 'reddit.com';
    case 'quora':
      return 'quora.com';
    case 'linkedin_post':
    case 'linkedin_article':
      return 'linkedin.com';
    case 'medium':
      return 'medium.com';
    case 'dev_to':
      return 'dev.to';
    case 'x_thread':
      return 'x.com';
  }
}

/**
 * Cheap host check for the Mark-posted modal. We match either the exact
 * host or a `.<host>` suffix so LinkedIn regional domains (`de.linkedin.com`)
 * and Medium publications (`open.medium.com`) still pass. The server does
 * the authoritative check — this exists so the user doesn't wait for a
 * round-trip to learn they typed the wrong domain.
 */
export function hostMatchesPlatform(
  url: string,
  platform: OffPageBriefPlatform,
): boolean {
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return false;
  }
  // X threads also accept twitter.com per the API's allow-list.
  const allowed =
    platform === 'x_thread' ? ['x.com', 'twitter.com'] : [canonicalHostForPlatform(platform)];
  return allowed.some((h) => host === h || host.endsWith(`.${h}`));
}

/**
 * Badge color for the per-variant quality score. Buckets match the spec:
 *   ≥85 = ready (green), 70–84 = needs polish (amber), <70 = not ready.
 */
export function qualityBadgeVariant(
  score: number,
): 'success' | 'warning' | 'destructive' {
  if (score >= 85) return 'success';
  if (score >= 70) return 'warning';
  return 'destructive';
}

/** Sort order for grouping variants in the left rail. */
export const PLATFORM_ORDER: OffPageBriefPlatform[] = [
  'reddit',
  'quora',
  'linkedin_post',
  'linkedin_article',
  'medium',
  'dev_to',
  'x_thread',
];
